import type { ApiResponse } from "../../../types/shared";
import httpClient from "../../../utils/httpClient";
import type {
  Campaign,
  CreateCampaignParams,
  UpdateCampaignParams,
  DeleteCampaignParams,
} from "../interfaces";

interface ICampaignService {
  createCampaign(params: CreateCampaignParams): Promise<Campaign>;
  getAllCampaigns(): Promise<Campaign[]>;
  getCampaignById(id: number): Promise<Campaign>;
  updateCampaign(params: UpdateCampaignParams): Promise<Campaign>;
  deleteCampaign(params: DeleteCampaignParams): Promise<{
    message: string;
    id: number;
  }>;
}

class CampaignService implements ICampaignService {
  private base = "/v1/campaigns";

  createCampaign = async (
    params: CreateCampaignParams
  ): Promise<Campaign> => {
    const formData = new FormData();

    formData.append("title", params.title);
    formData.append("description", params.description);

    if (params.config) {
      formData.append("config", JSON.stringify(params.config));
    }

    if (params.images && params.images.length > 0) {
      params.images.forEach((image) => {
        formData.append("images", image);
      });
    }

    if (params.videoFiles && params.videoFiles.length > 0) {
      params.videoFiles.forEach((video) => {
        formData.append("videos", video);
      });
    }

    const res = await httpClient.post<ApiResponse<Campaign>>(
      this.base,
      formData
    );

    return res.data.data;
  };

  getAllCampaigns = async (): Promise<Campaign[]> => {
    const res = await httpClient.get<ApiResponse<Campaign[]>>(this.base);

    return res.data.data;
  };

  getCampaignById = async (id: number): Promise<Campaign> => {
    const res = await httpClient.get<ApiResponse<Campaign>>(
      `${this.base}/${id}`
    );

    return res.data.data;
  };

  updateCampaign = async (
    params: UpdateCampaignParams
  ): Promise<Campaign> => {
    const formData = new FormData();

    formData.append("id", params.id.toString());

    if (params.title != null) {
      formData.append("title", params.title);
    }

    if (params.description != null) {
      formData.append("description", params.description);
    }

    if (params.config) {
      formData.append("config", JSON.stringify(params.config));
    }

    if (params.images && params.images.length > 0) {
      params.images.forEach((image) => {
        formData.append("images", image);
      });
    }

    if (params.videoFiles && params.videoFiles.length > 0) {
      params.videoFiles.forEach((video) => {
        formData.append("videos", video);
      });
    }

    const res = await httpClient.put<ApiResponse<Campaign>>(
      this.base,
      formData
    );

    return res.data.data;
  };

  deleteCampaign = async (
    params: DeleteCampaignParams
  ): Promise<{ message: string; id: number }> => {
    const res = await httpClient.delete<
      ApiResponse<{ message: string; id: number }>
    >(this.base, {
      data: { id: params.id },
    });

    return res.data.data;
  };
}

export const campaignService = new CampaignService();
